/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MenuBar from '../../MenuBar/MenuBar';
import cytoscape from 'cytoscape';
import klay from 'cytoscape-klay';
import dagre from 'cytoscape-dagre';
import cola from 'cytoscape-cola';
import bilkent from 'cytoscape-cose-bilkent';
import euler from 'cytoscape-euler';
import fcose from 'cytoscape-fcose';
import cise from 'cytoscape-cise';
import expandCollapse from 'cytoscape-expand-collapse';
import { contextMenu } from './Graph.view.contextmenu';
import { run as runActions } from '../../../store/workspaces/workspaces/run';
import onLoad from './events/onLoad';
import tapNode from './events/tap.node';
import tabContext from './events/tab.context';
import tapEdge from './events/tab.edge';
import boxSelect from './events/boxSelect';
import { WidgetProvider } from '../WidgetContext';
import { useTranslation } from 'react-i18next';
import { mouseoverNode, mouseoverEdge, mouseoutNode, mouseoutEdge, mouseDown, mouseUp } from './events/mouseEvents';
import { dragPan } from './events/drag';
import { grabElement } from './events/grab';
import { dropElement } from './events/drop';
import { WidgetContainerStyled, WidgetContentStyled } from '../styles';
import { getStyles } from './services/getStyles';
import { showTooltip } from './services/tooltips';
import PropTypes from 'prop-types';
import 'cytoscape-context-menus/cytoscape-context-menus.css';
import './styles.css';
// import CollisionToggle from './components/CollisionToggle';
import { toast } from 'src/utils/notification';
import { addNodesCytoscape } from './services/addNodes';
import { removeNode } from './services/removeNodes';
import { applyKHop as applyKHopSvc, clearKHop as clearKHopSvc } from './services/khop';
import { toggleFullscreen as toggleFullscreenSvc } from './services/fullscreen';
import { getAllNodesAndEdgesSvc } from './services/getNodesAndEdges';
import { reloadLayoutSvc } from './services/reloadLayout';
import { zoomFitNodesSvc } from './services/zoomFit';
import { getGraphData } from 'src/api/query/static/run.style';
import { makeGraphKey, saveState } from './graph.cache';
import GraphToolbar from './components/GraphToolbar';

const nodeHtmlLabel = require('cytoscape-node-html-label');
nodeHtmlLabel(cytoscape);

cytoscape.use(cise);
cytoscape.use(klay);
cytoscape.use(dagre);
cytoscape.use(cola);
cytoscape.use(bilkent);
cytoscape.use(euler);
cytoscape.use(fcose);
cytoscape.use(expandCollapse);

const GraphWidgetView = ({ navData, actionsState }) => {
  const cyRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const fsRootRef = useRef<HTMLDivElement | null>(null); // Fullscreen root wraps toolbar + graph
  const ecRef = useRef<any>(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const { t } = useTranslation();
  const [cyInstance, setCyInstance] = useState<any>(null);
  // const [avoidOverlap, setAvoidOverlap] = useState(false);
  const [kHop, setKHop] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutName, setLayoutName] = useState<any>(navData?.widgetData?.config?.layout ?? 'dagre');
  const [graphData, setGraphData] = useState(null);
  const [showToolbar, setShowToolbar] = useState(null);

  let cy: any;
  let contextMenuInstance: any;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getGraphData({ ...actionsState, widgetKey: navData.renderID }, navData.widgetData);
        setGraphData(response.objects);
      } catch (error) {
        toast.error('Error fetching graph data:', error.message);
        throw error;
      }
    };
  
    fetchData();
    return () => {
      setGraphData(null);
    };
  }, [navData.renderID, navData.widgetData.dataID, navData.widgetData.timestamp]);

  useEffect(() => {
    return () => {
      try {
        if (cyInstance) {
          const key = makeGraphKey(navData, navData.widgetData);
          saveState(key, cyInstance); 
        }
      } catch {}
    };
  }, [cyInstance, navData.renderID, navData.widgetData?.dataID]);


  useEffect(() => {
    contextMenuInstance?.destroy();
    renderCytoscapeElement(graphData, navData.widgetData.config);
    return () => {
      contextMenuInstance?.destroy();
      localStorage.setItem('selectedElements', JSON.stringify([]));
    };
  }, [navData.widgetData, graphData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Del') removeNode(cy, {});
    };
    window.addEventListener('keydown', handleKeyDown);

    const fsHandler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', fsHandler);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', fsHandler);
    };
  }, []);

  useEffect(() => {
    if (!cyInstance) return;
    try {
      const prev = cyInstance.scratch('_layout') || {};
      cyInstance.scratch('_layout', { ...prev, name: layoutName });
    } catch {}
  }, [layoutName, cyInstance]);

  const getAllNodesAndEdges = useCallback(async () => { return getAllNodesAndEdgesSvc(cy);}, [cy]);
  const reloadLayout = useCallback(async (layout) => { reloadLayoutSvc(cy, layout);}, [cy]);
  const zoomFitNodes = useCallback(() => { zoomFitNodesSvc(cy); }, [cy]);
  const toggleFullscreen = async () => { await toggleFullscreenSvc(fsRootRef.current); }; 
  const applyKHop = (k: number) => {applyKHopSvc(cyInstance, k, setKHop, toast);};
  const clearKHop = () => { clearKHopSvc(cyInstance, setKHop);};

  // Kollisions-Schalter (cola avoidOverlap)
  // const toggleAvoidOverlap = () => {
  //   if (!cyInstance) return;
  //   const next = !avoidOverlap;
  //   setAvoidOverlap(next);
  //   cyInstance.layout({
  //     name: 'cola',
  //     avoidOverlap: next,
  //     animate: true,
  //     fit: true,
  //     maxSimulationTime: 1500,
  //     refresh: 1,
  //   }).run();
  // };
  // ---- Cytoscape Setup ----
  const renderCytoscapeElement = async (elementsData, config) => {
    const style: cytoscape.StylesheetJson = await getStyles(config, navData.widgetData.style);

    // Ensure the container ref is mounted before initialising Cytoscape
    if (!cyRef.current) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    if (!cyRef.current) {
      console.warn('Cytoscape container not ready yet; aborting init');
      return;
    }

    cy = cytoscape({
      container: cyRef.current as HTMLDivElement,
      style,
      elements: elementsData,
      wheelSensitivity: 1,
      boxSelectionEnabled: true,
      layout: {
        name: layoutName,
        fit: true,
        spacingFactor: 1.2,
        randomize: config.randomize,
        animate: config.animate,
      },
    });

    setCyInstance(cy);

    try {
      (cy as any).expandCollapse && ((ecRef.current = (cy as any).expandCollapse({
        cueEnabled: false,
        animate: true,
        fisheye: false,
      })));
    } catch (e) {
      console.warn('expandCollapse init failed', e);
    }

    cy.style().selector('.hidden-by-khop').style({ display: 'none' }).update();

    cy.nodeHtmlLabel([{
      query: 'node.selected',
      halign: 'center',
      valign: 'bottom',
      halignBox: 'center',
      valignBox: 'bottom',
      cssClass: 'cy-infoblock',
      tpl(data) { return data?.infoblock == null ? '' : data.infoblock; },
    }]);

    const tooltip: HTMLElement | null = document.getElementById('cy-tooltip');
    let tooltipTimeout: any;
    let tooltipHideTimeout: any;
    let isContextMenuOpen = false;

    const hideTooltip = () => { tooltip.classList.add('hidden');};
    const cancelTooltipTimers = () => { clearTimeout(tooltipTimeout); clearTimeout(tooltipHideTimeout);};
    const setContextMenuOpen = (open: boolean) => { isContextMenuOpen = open; cancelTooltipTimers(); hideTooltip(); };

    setTimeout(() => {
      const menuEl = document.querySelector('.cy-context-menus-cxt-menu') as HTMLElement | null;
      if (!menuEl) return;

      const onMenuClick = () => setTimeout(() => setContextMenuOpen(false), 0);
      menuEl.addEventListener('click', onMenuClick);

      const onDocClick = (ev: MouseEvent) => {
        if (!isContextMenuOpen) return;
        if (menuEl.contains(ev.target as Node)) return;
        setContextMenuOpen(false);
      };
      document.addEventListener('click', onDocClick);

      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setContextMenuOpen(false); };
      document.addEventListener('keydown', onKey);

      cy.once('destroy', () => {
        try {
          menuEl.removeEventListener('click', onMenuClick);
          document.removeEventListener('click', onDocClick);
          document.removeEventListener('keydown', onKey);
        } catch {}
      });
    }, 0);

    if (navData.widgetData.menu.context !== undefined)
      contextMenuInstance = await contextMenu(
        cy,
        navData.widgetData.menu.context,
        (nodes, position) => addNodesCytoscape(cy, nodes, position),
        (node) => {
          const nodeId = typeof node.id === 'function' ? node.id() : node.id;
          const resolvedId = typeof nodeId === 'function' ? nodeId() : nodeId;
          removeNode(cy, { id: resolvedId });
        },
        t
      );

    if (navData.widgetData.events?.length > 0) {
      cy.on('cxttapstart cxttap', () => setContextMenuOpen(true));
      cy.on('tap pan zoom boxstart boxend', () => setContextMenuOpen(false));

      // Doppelklick (Double-Tap) zeigt Tooltip exakt an der Klick-Position
      const showTooltipAtEvent = (e: any) => {
        cancelTooltipTimers();
        const crect = cy.container().getBoundingClientRect();
        const rp = e.renderedPosition || e.position || { x: 0, y: 0 };
        const pointer = { x: crect.left + rp.x, y: crect.top + rp.y };
        showTooltip(e.target, tooltip, pointer);
      };
      let lastTapAt = 0;
      let lastTapTargetId: string | null = null;
      const DOUBLE_TAP_MS = 300;
      cy.on('tap', 'node, edge', (e) => {
        const now = performance.now();
        const id = typeof e.target.id === 'function' ? e.target.id() : e.target.id;
        const isDouble = lastTapTargetId === id && (now - lastTapAt) < DOUBLE_TAP_MS;
        if (isDouble) {
          e.originalEvent?.preventDefault?.();
          e.stopPropagation?.();
          showTooltipAtEvent(e);
        }
        lastTapAt = now;
        lastTapTargetId = id;
      });

      await onLoad(cy);
      await tapNode(cy, navData.widgetData.events, runActions, actionsState);
      await tapEdge(cy, navData.widgetData.events, runActions, actionsState);
      await tabContext(cy, selectedElements);
      await mouseoverNode(cy);
      await mouseoverEdge(cy);
      await mouseoutNode(cy);
      await mouseoutEdge(cy);
      await boxSelect(cy, setSelectedElements);
      await mouseDown(cy, setSelectedElements);
      await dragPan(cy);
      await mouseUp(cy);
      await grabElement(cy);
      await dropElement(cy);
    }

    try {
      const prev = cy.scratch('_layout') || {};
      cy.scratch('_layout', { ...prev, name: layoutName, orientation: prev.orientation || 'LR' });
    } catch {}
  };

  if (!graphData || !navData.widgetData) return null;

  return (
    <WidgetProvider
      value={{
        getAllNodesAndEdges,
        widgetData: navData.widgetData,
        actionsState,
        reloadLayout,
        zoomFitNodes,
      }}
    >
      {navData.widgetData.menu && ( 
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 basis-0">
            <MenuBar
              key={`menu-${navData.widgetData.menu.id}`}
              menu={navData.widgetData.menu}
              widgetTools={null}
            />
          </div>
          <div className="flex-none pt-2">
            <button
              type="button"
              onClick={() => { setShowToolbar(!showToolbar); }}
              className="flex-none w-9 h-9 inline-flex items-center justify-center rounded-full border border-gray-300 bg-white/80 hover:bg-white text-gray-600 shadow-sm backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2"
            >
              <span className="e-icons e-settings text-gray-400" />
            </button>
          </div>
        </div>
      )}
      <div ref={fsRootRef} className="w-full h-full flex flex-col bg-white">
        <div className="flex-1 min-h-0">
          <WidgetContainerStyled>
            { showToolbar && <GraphToolbar
              cy={cyInstance}
              layoutName={layoutName}
              setLayoutName={setLayoutName}
              kHop={kHop}
              setKHop={setKHop}
              applyKHop={applyKHop}
              clearKHop={clearKHop}
              isFullscreen={isFullscreen}
              toggleFullscreen={toggleFullscreen}
              mode={'view'}
            />}
            <WidgetContentStyled>
              <div ref={wrapperRef} className="graph-container relative w-full h-full">
                <div className="w-full h-full">
                  <div className="node_selected w-full h-full">
                    <div className="w-full h-full " ref={cyRef} />
                  </div>
                </div>
                {/* Tooltip */}
                <div
                  id="cy-tooltip"
                  className="absolute hidden pointer-events-auto max-w-xs p-3 bg-white text-gray-800 text-sm rounded-md shadow-lg border border-gray-300 space-y-1 z-50"
                />
              </div>
            </WidgetContentStyled>
          </WidgetContainerStyled>
        </div>
      </div>
    </WidgetProvider>
  );
};

GraphWidgetView.propTypes = {
  navData: PropTypes.object,
  actionsState: PropTypes.object,
  renderData: PropTypes.object,
  graphData: PropTypes.any,
};

export default GraphWidgetView;
